import { observer } from "mobx-react-lite";
import { useCallback, useEffect } from "react";
import { useMap } from "react-map-gl";
import { useClosableStore, useFilterSettingsStore, useListStore, useMapStore, useSearchStore, useSegmentedButtonsStore } from "..";
import BaseMap from "../interfaces/BaseMap";
import SearchType from "../interfaces/SearchType";
import { ClosableOrder, IClosableItem } from "../store/ClosableStore";

function KeyboardShortcuts() {
    const map = useMap().map!;
    const searchStore = useSearchStore();
    const mapStore = useMapStore();
    const filterSettingsStore = useFilterSettingsStore();
    const closableStore = useClosableStore();
    const listStore = useListStore();
    const segmentedButtonsStore = useSegmentedButtonsStore();

    const onKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.ctrlKey || e.metaKey)
            return;

        if (e.key === "Escape") {
            if (closableStore.items.some(isHighOrClickClosableOrder)) {
                closableStore.items.filter(isHighOrClickClosableOrder).forEach(item => item.onClose());
            } else if (closableStore.items.some(isMediumClosableOrder)) {
                closableStore.items.filter(isMediumClosableOrder).forEach(item => item.onClose());
            } else if (closableStore.items.some(isLowClosableOrder)) {
                closableStore.items.filter(isLowClosableOrder).forEach(item => item.onClose());
            }
        } else if (e.key === "Tab") {
            if (segmentedButtonsStore.buttons && !segmentedButtonsStore.isTabSwitchDisabled) {
                segmentedButtonsStore.switchButton(e.shiftKey);
                e.preventDefault();
            }
        }

        const activeElement = document.activeElement;
        if (activeElement && ['input', 'textarea', 'select'].includes(activeElement.tagName.toLowerCase()))
            return;

        if (e.key === "1") {
            mapStore.setBaseMap(BaseMap.Streets);
        } else if (e.key === "2") {
            mapStore.setBaseMap(BaseMap.SatelliteMapbox);
        } else if (e.key === "3") {
            mapStore.setBaseMap(BaseMap.SatelliteGoogle);
        } else if (e.key === "4") {
            mapStore.setBaseMap(BaseMap.SatelliteArcGIS);
        } else if (e.key === "5") {
            mapStore.setBaseMap(BaseMap.SatelliteBing);
        } else if (e.key === "6") {
            mapStore.setBaseMap(BaseMap.SatelliteNAIP);
        } else if (e.key === "0") {
            mapStore.setBaseMap(BaseMap.HybridMapbox);
        } else if (e.key === "a") {
            mapStore.setAllLayersVisible();
        } else if (e.key === "s") {
            mapStore.setDealsAndIOSZoningLayersVisible();
        } else if (e.key === "z") {
            mapStore.setNoneLayersVisible();
        } else if (e.key === "d") {
            e.preventDefault();
            listStore.toggleDeals();
        } else if (e.key === "c") {
            e.preventDefault();
            listStore.toggleLeaseComps();
        } else if (e.key === "v") {
            e.preventDefault();
            listStore.toggleSaleComps();
        } else if (e.key === "f") {
            filterSettingsStore.open();
        } else if (e.key === "/") {
            e.preventDefault();
            searchStore.setSearch(searchStore.query, SearchType.Address);
        } else if (e.key === ".") {
            e.preventDefault();
            searchStore.setSearch(searchStore.query, SearchType.Deals);
        } else if (e.key === ",") {
            e.preventDefault();
            searchStore.setSearch(searchStore.query, SearchType.Leads);
        } else if (e.key === "+") {
            map?.zoomIn();
        } else if (e.key === "-") {
            map?.zoomOut();
        }
    }, [map]);

    const onMouseDown = useCallback((e: MouseEvent) => {
        closableStore.items.filter(isClickClosableOrder).forEach(item => item.onClose());
    }, []);

    useEffect(() => {
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [onKeyDown]);

    useEffect(() => {
        window.addEventListener("mousedown", onMouseDown);
        return () => window.removeEventListener("mousedown", onMouseDown);
    }, [onMouseDown]);

    return null;
}

export default observer(KeyboardShortcuts);

const isClickClosableOrder = (item: IClosableItem) => item.order === ClosableOrder.Click;
const isHighOrClickClosableOrder = (item: IClosableItem) => item.order === ClosableOrder.Click || item.order === ClosableOrder.High;
const isMediumClosableOrder = (item: IClosableItem) => item.order === ClosableOrder.Medium;
const isLowClosableOrder = (item: IClosableItem) => item.order === ClosableOrder.Low;